import { useState } from 'react';
import { parseISO } from 'date-fns';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { usePatientPerformedTreatmentsQuery } from '@/hooks/useSessionNoteQueries';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';

interface PerformedTreatmentsSectionProps {
  patientId: string;
}

function resolveOutcomeTone(outcome: string): BadgeTone {
  if (outcome === 'refused') {
    return 'danger';
  }
  if (outcome === 'skipped') {
    return 'warning';
  }
  return 'success';
}

export function PerformedTreatmentsSection({ patientId }: PerformedTreatmentsSectionProps) {
  const { t } = useTranslation('patients');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const treatmentsQuery = usePatientPerformedTreatmentsQuery(patientId, page);
  const treatments = treatmentsQuery.data === undefined ? [] : treatmentsQuery.data.items;
  const totalItems = treatmentsQuery.data === undefined ? 0 : treatmentsQuery.data.totalItems;

  function openSessionNote(sessionNoteId: string) {
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
        patientId,
        noteId: sessionNoteId,
      })
    );
  }

  return (
    <RelatedRecordsSection
      title={t('treatmentsTab.performedTitle')}
      isLoading={treatmentsQuery.isLoading}
      isError={treatmentsQuery.isError}
      onRetry={function retry() {
        treatmentsQuery.refetch();
      }}
    >
      {treatments.length === 0 ? (
        <div className="py-2">
          <p className="text-sm text-zoomuted">{t('treatmentsTab.performedEmpty')}</p>
          <p className="text-xs text-zoomuted mt-1">
            {t('treatmentsTab.performedEmptyDescription')}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {treatments.map(function renderTreatment(treatment) {
              return (
                <li key={treatment.id}>
                  <button
                    type="button"
                    onClick={function open() {
                      openSessionNote(treatment.sessionNoteId);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-zooborder text-left hover:border-zooprimary transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zooindigo truncate">
                        {treatment.treatmentName}
                      </p>
                      <p className="text-xs text-zoomuted">
                        {formatDateToDayMonthYear(parseISO(treatment.performedOn))}
                      </p>
                    </div>
                    <Badge tone={resolveOutcomeTone(treatment.outcome)} size="sm">
                      {t(
                        'sessionNote.outcome' +
                          treatment.outcome.charAt(0).toUpperCase() +
                          treatment.outcome.slice(1)
                      )}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </RelatedRecordsSection>
  );
}

